import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {squash} from '../../common/rxjs/squash';
import {sortedIndexByComparator} from '../../common/sorted-index-by-comparator';
import {destroy$} from '../../components/destroyable';
import {DexMdSubjectService} from '../../components/dex/dex-streaming/subjects/dex-md-subject-service';
import {RefRate} from '../../components/dex/dex-streaming/types/ref-rate';
import {Widget} from '../../components/layout/widget/widget';
import {WIDGET_UPDATES_GATING$} from '../../components/layout/widget/widget-updates-gating';
import {PARTIAL_LOADING, PartialLoading} from '../../components/partial-loading-indicator';
import {CellSizeObserverService} from '../../pages/dashboard/layout/cell/cell-size-observer.service';
import {DEX_DEV_RATES_TABLE_COLUMNS_DICT, DexDevRatesTable, DexDevRatesTableColumnConfig} from './dex-dev-rates-table';
import {DexDevRatesWidget} from './dex-dev-rates-widget';
import {DexDevRatesWidgetImplementation} from './dex-dev-rates-widget.implementation';
import {DexDisplayRateFactory, DisplayDexRate} from './dex-display-rate.factory';
import {DexDevRatesWidgetSettings} from './settings/dex-dev-rates-widget-settings';

const EMPTY_ROW = {} as DisplayDexRate;

const rowsComparator = (a: DisplayDexRate, b: DisplayDexRate): number =>
    (b.tranche && 1 || 0) - (a.tranche && 1 || 0)
    || a.pair.localeCompare(b.pair);

@Component({
    selector:        'dex-dev-rates-widget',
    templateUrl:     './dex-dev-rates-widget.component.html',
    styleUrls:       ['./dex-dev-rates-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers:       [
        DexDisplayRateFactory
    ],
    standalone:      false
})
export class DexDevRatesWidgetComponent implements OnInit {

    @ViewChild('loadingIndicator', {read: PARTIAL_LOADING, static: true})
    public loadingIndicator: PartialLoading;

    public readonly emptyRow = EMPTY_ROW;

    public readonly destroy$ = destroy$();

    public readonly table: DexDevRatesTable = {
        columnsDict:    DEX_DEV_RATES_TABLE_COLUMNS_DICT,
        columnsConfig:  [],
        rows:           [],
        emptyRow:       EMPTY_ROW,
        columnsUpdate$: new Subject<DexDevRatesTableColumnConfig[]>()
    };

    public readonly resize$ = this.cellSizeObserverService.resize$;

    public readonly widgetImpl: DexDevRatesWidgetImplementation = this.widget.implementationInstance;

    private settingsWithUpdates$: Observable<Partial<DexDevRatesWidgetSettings>> = this.widget.settingsWithUpdates$.pipe(
        takeUntil(this.destroy$)
    );

    constructor(
        @Inject(Widget)
        public widget: DexDevRatesWidget,

        private changeDetectorRef: ChangeDetectorRef,
        private cellSizeObserverService: CellSizeObserverService,
        private dexMdSubjectService: DexMdSubjectService,
        private dexDisplayRateFactory: DexDisplayRateFactory
    ) {}

    public ngOnInit(): void {
        this.listenToSettingsChanges();
        this.listenToTableEvents();
        this.initTableData();
        this.listenToRateUpdates();
    }

    private initTableData(): void {
        const rows: DisplayDexRate[] = this.dexDisplayRateFactory.createDisplayRates(
            [...this.dexMdSubjectService.refRateMapByPair.values()]
        );

        rows.sort(rowsComparator);

        this.table.rows = rows;

        console.warn(rows); // TODO
    }

    private updateRows(rates: RefRate[]): void {
        const rows: DisplayDexRate[] = [...this.table.rows];

        for (const r of rates) {
            const row = this.dexDisplayRateFactory.createDisplayRate(r);

            let rIndex = rows.findIndex(oldRow => oldRow.pair === row.pair);

            if (rIndex >= 0) {
                rows[rIndex] = row;
            } else {
                rIndex = sortedIndexByComparator(rows, row, rowsComparator, true);
                rows.splice(rIndex, 0, row);
            }
        }

        this.table.rows = rows;
        this.changeDetectorRef.markForCheck();
    }

    private listenToRateUpdates(): void {
        this.dexMdSubjectService.refRateUpdated$.pipe(
            squash({
                closingNotifier: WIDGET_UPDATES_GATING$,
                groupBy:         (r: RefRate) => [r.baseDeliverableId, r.quoteDeliverableId].join('/')
            }),
            tap(rates => this.updateRows(rates)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private listenToSettingsChanges(): void {
        this.settingsWithUpdates$.subscribe(settings => {
            if (settings.columns) {
                this.table.columnsConfig = settings.columns;
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    private listenToTableEvents(): void {
        this.table.columnsUpdate$.pipe(
            switchMap(() => this.widgetImpl.updateSettings({
                columns: this.table.columnsConfig
            }))
        ).subscribe();
    }

}
